import React, {FC, useMemo, useRef} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageTypesEnum} from '../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {RecipientRolesEnum} from '../../../../../libs/shared/core/src/models/api/recipientModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {buildVisualRecipientForEmail} from '../../../../helpers/models/recipientParsers';
import {AppState} from '../../../../store/appState';
import {makeGetHasComposeChannelExcludingDemo} from '../../../../store/data/channels/channelsSelectors';
import {composeMessageAction} from '../../../../store/data/messages/draftMessagesActions';
import {navigateTopBarPopoverAction} from '../../../../store/panels/app/appPanelActions';
import {makeGetIsHelpPopoverVisible} from '../../../../store/panels/app/appPanelSelectors';
import {AppPanelPopoverTypesEnum} from '../../../../store/panels/app/appPanelState';
import {
  helpCenterUrl,
  lmsFrontAcademyUrl,
  navigateToLinkAction,
} from '../../../../store/stateless/navigationActions';
import {TopBarButton} from '../topBarButton';
import {SettingsHelpWidget} from './settingsHelpWidget';

/*
 * Constants.
 */

const supportEmailRecipient = buildVisualRecipientForEmail(RecipientRolesEnum.TO, 'support@frontapp.com');

interface HelpProps {
  panelId: AppPanelId;
}

/*
 * Component.
 */

export const Help: FC<HelpProps> = ({panelId}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const getIsHelpPopoverVisible = useMemo(() => makeGetIsHelpPopoverVisible(), []);
  const isDropdownVisible = useAppSelector((state: AppState) => getIsHelpPopoverVisible(state, {panelId}));
  const hasComposeChannelExcludingDemo = useAppSelector(
    makeGetHasComposeChannelExcludingDemo<void>({
      getSpecifiedMessageType: () => MessageTypesEnum.EMAIL,
    }),
  );

  const dispatch = useAppDispatch();
  const helpLabel = <Trans id="self-serve-help-button">Help</Trans>;

  const onHelpClick = () => {
    dispatch(navigateTopBarPopoverAction(panelId, AppPanelPopoverTypesEnum.HELP));
  };

  const onRequestClose = () => {
    dispatch(navigateTopBarPopoverAction(panelId, undefined));
  };

  const navigateTo = (link: string, interfaceEvent?: InterfaceEventsEnum) => {
    // Close the dropdown.
    onRequestClose();

    // Track the event.
    if (interfaceEvent) {
      trackInterfaceEvent(interfaceEvent);
    }

    // Navigate to the link.
    dispatch(navigateToLinkAction(link));
  };

  const onEmailClick = () => {
    // Close the dropdown.
    onRequestClose();

    // Track the event.
    trackInterfaceEvent(InterfaceEventsEnum.HELP_WIDGET_SUPPORT_CLICK);

    // Open composer.
    dispatch(
      composeMessageAction({
        recipients: [supportEmailRecipient],
        shouldForcePopout: true,
      }),
    );
  };

  return (
    <>
      <TopBarButton
        ref={buttonRef}
        iconName="help"
        isSelected={isDropdownVisible}
        renderTooltip={() => helpLabel}
        interactionId="p0-settings-help"
        onClick={onHelpClick}
      >
        {helpLabel}
      </TopBarButton>
      {isDropdownVisible && (
        <SettingsHelpWidget
          panelId={panelId}
          anchor={buttonRef}
          useDropdownStyle
          isGlobalAdmin={isGlobalAdmin}
          onFrontAcademyClick={() => {
            navigateTo(lmsFrontAcademyUrl);
            trackInterfaceEvent(InterfaceEventsEnum.P0_LMS_CLICK, {location: 'help_tip'});
          }}
          onHelpClick={() => {
            navigateTo(helpCenterUrl);
            trackInterfaceEvent(InterfaceEventsEnum.P0_HELP_CENTER_CLICK, {location: 'help_tip'});
          }}
          canEmail={hasComposeChannelExcludingDemo}
          onEmailClick={onEmailClick}
          onOpenClick={onOpenClick}
          onRequestClose={onRequestClose}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

const onOpenClick = () => {
  trackInterfaceEvent(InterfaceEventsEnum.HELP_WIDGET_OPEN_CLICK);
};
